import { useEffect, useMemo, useState } from 'react'

import Icon from '../Icon'

import SelectMenuItem from './SelectMenuItem'
import { SelectOption } from './types'

const defaultFilterOption = <TValue,>(searchQuery: string | null, option: SelectOption<TValue>) =>
  option.label.toLocaleLowerCase().includes(searchQuery?.toLocaleLowerCase() ?? '')

/**
 * Sort options alphabetically by `label` within each contiguous run of
 * options that share a `group` value. Preserving contiguity matters because
 * `SelectMenuItemGroupLabel` decides whether to print a group header by
 * comparing each option's `group` to the previous option's `group` — a
 * naive global sort would interleave groups and produce duplicate or
 * misplaced headers. Group order itself follows first appearance in the
 * input array, so callers control the group ordering and this helper only
 * affects intra-group order.
 *
 * Exposed as a caller-side helper rather than a `Select` prop because
 * govwell-ui components stay un-opinionated about display order: the
 * Select renders whatever options the caller hands it, and any
 * alphabetization is an explicit, easy-to-grep step at the call site.
 */
export const sortWithinContiguousGroups = <TValue, TOption extends SelectOption<TValue>>(
  options: TOption[]
): TOption[] => {
  if (options.length <= 1) {
    return options
  }
  const result: TOption[] = []
  let bucket: TOption[] = []
  let currentGroup: string | undefined = options[0]?.group
  const flushBucket = () => {
    bucket.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    result.push(...bucket)
    bucket = []
  }
  for (const option of options) {
    if (option.group !== currentGroup) {
      flushBucket()
      currentGroup = option.group
    }
    bucket.push(option)
  }
  flushBucket()
  return result
}

export const useFilteredOptions = <TValue,>(args: {
  isAsync: boolean
  filterOption?: (searchQuery: string, option: SelectOption<TValue>) => boolean
  searchQuery: string | null | undefined
  options: SelectOption<TValue>[]
}) => {
  const { isAsync, filterOption, options, searchQuery } = args
  return useMemo((): SelectOption<TValue>[] => {
    if (isAsync) {
      return options
    }
    return options.filter((o) => (filterOption ?? defaultFilterOption)(searchQuery ?? '', o))
  }, [filterOption, isAsync, options, searchQuery])
}

export const useCreateOption = (args: {
  activeIndex: number
  createActionIndex: number
  isCreatable: boolean | undefined
  onCreateOption: (() => void) | undefined
  searchQuery: string | null | undefined
  setActiveIndex: (index: number) => void
}) => {
  const {
    activeIndex,
    createActionIndex,
    isCreatable,
    onCreateOption,
    searchQuery,
    setActiveIndex,
  } = args

  return useMemo(() => {
    if (!isCreatable || !searchQuery) {
      return null
    }
    return (
      <SelectMenuItem
        id="create-option"
        key="create-option"
        onClick={onCreateOption}
        onMouseOver={() => setActiveIndex(createActionIndex)}
        {...(activeIndex === createActionIndex
          ? {
              ['data-selected']: true, // controls option remaining highlighted when submenu is expanded
              ['aria-selected']: true,
            }
          : {})}
      >
        <Icon.FaPlus /> {`Create "${searchQuery}"`}
      </SelectMenuItem>
    )
  }, [isCreatable, searchQuery, onCreateOption, activeIndex, createActionIndex, setActiveIndex])
}

export const useShowAsyncStartSearchingEmptyState = (args: {
  isAsync: boolean
  isLoading: boolean | null | undefined
  searchQuery: string | null | undefined
}) => {
  const { isAsync, isLoading, searchQuery } = args
  const [showAsyncStartSearchingEmptyState, setShowAsyncStartSearchingEmptyState] = useState(false)

  useEffect(() => {
    if (!isAsync) {
      setShowAsyncStartSearchingEmptyState(false)
      return
    }

    if (!searchQuery) {
      setShowAsyncStartSearchingEmptyState(true)
      return
    }
    if (isLoading && searchQuery) {
      setShowAsyncStartSearchingEmptyState(false)
      return
    }
  }, [isAsync, isLoading, searchQuery])

  return showAsyncStartSearchingEmptyState
}
